import type { Base64EncodedString } from '@bonsai-components/utility-types';
import { useRouter } from 'next/router';
import {
  createContext,
  type FunctionComponent,
  type ReactNode,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react';
import {
  useDefaultOrganizationQuery,
  useSimpleOrganizationByIdQuery
} from '../__generated__/apollo-hooks';
import { fromBase64 } from '../helpers/encoding.helper';
import { logError } from '../helpers/logger.helper';
import { getOrganizationDisplayName } from '../helpers/organizations.helper';
import {
  deriveSelectedOrganization,
  type SelectedOrganizationState
} from '../helpers/selected-organization.helper';
import { useSelectedOrganizationStore } from '../stores/organization-store';
import { NotificationContext } from './notification.context';

// Routes whose `?organizationId=` is a base64 org to select. Others (commits,
// visualizations) use the same name for a raw id scoping their own query.
const DEEP_LINK_PATHNAMES = new Set([
  '/recipes/[recipeId]',
  '/builder/[recipeRunId]'
]);

const NOTIFICATION_PLACEMENT = {
  placement: { horizontal: 'center', vertical: 'top' }
} as const;

type DeepLinkQueryParams = {
  organizationId?: Base64EncodedString;
  trial?: string;
};

const OrganizationContext = createContext<
  SelectedOrganizationState | undefined
>(undefined);

/**
 * Resolves the selected organization once for the whole app, preferring a
 * deep-linked `?organizationId=`, then the stored id, then the user's default.
 */
export const OrganizationProvider: FunctionComponent<{
  children: ReactNode;
}> = ({ children }) => {
  const router = useRouter();
  const { isReady, pathname } = router;
  const { renderNotification } = useContext(NotificationContext);
  const selectedOrganizationId = useSelectedOrganizationStore(
    (state) => state.selectedOrganizationId
  );
  const update = useSelectedOrganizationStore((state) => state.update);

  const { organizationId: encodedDeepLinkId, trial } =
    router.query as DeepLinkQueryParams;
  const deepLinkId =
    isReady && encodedDeepLinkId && DEEP_LINK_PATHNAMES.has(pathname)
      ? fromBase64(encodedDeepLinkId)
      : undefined;
  // State, not a ref: the deep link stops gating consumers as soon as it is
  // handled, even if the `router.replace` that strips it fails.
  const [handledDeepLinkId, setHandledDeepLinkId] = useState<string>();
  const deepLinkPending =
    Boolean(deepLinkId) && handledDeepLinkId !== deepLinkId;

  // cache-first on both, so pollConversation's Organization cache writes
  // don't refetch simpleOrganizationById.
  const byId = useSimpleOrganizationByIdQuery({
    variables: { id: selectedOrganizationId },
    skip: !isReady || !selectedOrganizationId,
    fetchPolicy: 'cache-first',
    nextFetchPolicy: 'cache-first'
  });
  const deepLink = useSimpleOrganizationByIdQuery({
    variables: { id: deepLinkId },
    skip: !deepLinkPending,
    fetchPolicy: 'cache-first',
    nextFetchPolicy: 'cache-first'
  });

  const defaultOrganization = useDefaultOrganizationQuery({
    skip: !isReady || Boolean(selectedOrganizationId) || deepLinkPending,
    // Kept out of the cache so its write doesn't broadcast to `byId`.
    fetchPolicy: 'no-cache'
  });

  const defaultOrganizationData = defaultOrganization.data;
  useEffect(() => {
    if (selectedOrganizationId || deepLinkPending || !defaultOrganizationData) {
      return;
    }
    const defaultOrganizationId =
      defaultOrganizationData.organizations?.edges?.at(0)?.node?.id;
    if (defaultOrganizationId) {
      update({ selectedOrganizationId: defaultOrganizationId });
    } else {
      logError(new Error('No organizations returned for the default'));
    }
  }, [
    selectedOrganizationId,
    deepLinkPending,
    defaultOrganizationData,
    update
  ]);

  const deepLinkSettled =
    deepLinkPending &&
    !deepLink.loading &&
    (deepLink.data !== undefined || Boolean(deepLink.error));
  const deepLinkOrganization = deepLink.data?.organization;
  const deepLinkFailed = Boolean(deepLink.error) && !deepLinkOrganization;
  useEffect(() => {
    if (!deepLinkId) {
      setHandledDeepLinkId(undefined);
      return;
    }
    if (!deepLinkSettled) {
      return;
    }
    setHandledDeepLinkId(deepLinkId);
    if (deepLinkFailed) {
      // Leave the param so a reload retries the same link.
      renderNotification(
        'error',
        `The ${deepLinkId} organization couldn't be loaded. Reload to try again.`,
        NOTIFICATION_PLACEMENT
      );
      return;
    }
    if (!deepLinkOrganization) {
      renderNotification(
        'error',
        `The ${deepLinkId} organization was not found.`,
        NOTIFICATION_PLACEMENT
      );
    } else if (deepLinkOrganization.id !== selectedOrganizationId) {
      update({ selectedOrganizationId: deepLinkOrganization.id });
      // The trial landing explains orgs later; a toast about one confuses.
      if (trial !== '1') {
        renderNotification(
          'success',
          `The ${getOrganizationDisplayName(deepLinkOrganization)} organization is now selected.`,
          NOTIFICATION_PLACEMENT
        );
      }
    }
    const url = new URL(window.location.href);
    url.searchParams.delete('organizationId');
    router.replace(url).catch(logError);
  }, [
    deepLinkId,
    deepLinkSettled,
    deepLinkFailed,
    deepLinkOrganization,
    selectedOrganizationId,
    trial,
    renderNotification,
    router,
    update
  ]);

  const value = useMemo(
    () =>
      deriveSelectedOrganization({
        selectedOrganizationId,
        deepLinkPending,
        byId: {
          data: byId.data,
          previousData: byId.previousData,
          loading: byId.loading,
          error: byId.error
        },
        defaultOrganization: {
          data: defaultOrganization.data,
          loading: defaultOrganization.loading,
          error: defaultOrganization.error
        }
      }),
    [
      selectedOrganizationId,
      deepLinkPending,
      byId.data,
      byId.previousData,
      byId.loading,
      byId.error,
      defaultOrganization.data,
      defaultOrganization.loading,
      defaultOrganization.error
    ]
  );

  return (
    <OrganizationContext.Provider value={value}>
      {children}
    </OrganizationContext.Provider>
  );
};

export const useSelectedOrganization = (): SelectedOrganizationState => {
  const context = useContext(OrganizationContext);
  if (!context) {
    throw new Error(
      'useSelectedOrganization must be used within an OrganizationProvider'
    );
  }
  return context;
};
